import React, { useEffect } from 'react';
import { RouteComponentProps, useLocation } from 'react-router';
import UnauthenticatedPageContainer from './YSaatioUnauthenticatedPageContainer';
import YSaatioStrongAuthStep from './YSaatioStrongAuthStep';
import YSaatioSetCredentialsStep from './YSaatioSetCredentialsStep';
import YSaatioOneTimePasswordStep from './YSaatioOneTimePasswordStep';
import YSaatioSuccessStep from './YSaatioSuccessStep';
import { LoginCredentials } from '../../apis_kenno';
import { loginUser } from '../../context/kennoTenantActions';
import { useKennoTenantDispatch } from '../../context/kennoTenantContext';
import { YSaatioRoutes } from '../../constants/routes';
import { scrollToTop } from '../../utils';
import axios from 'axios';
import { SignInPageLocationState } from '../../types/exceptions';

const REGISTER_SESSION_ID_KEY = 'register_session_id';

const YSaatioSignUpPage: React.FC<RouteComponentProps> = ({ history }) => {
  const location = useLocation();
  const dispatch = useKennoTenantDispatch();
  const [oauthState, setOauthState] = React.useState<string | null>(null);
  const [credentials, setCredentials] = React.useState<LoginCredentials | null>();
  const [showSuccessStep, setShowSuccessStep] = React.useState<boolean>(false);
  const [registerSessionId, setRegisterSessionId] = React.useState<string | null>(null);

  useEffect(() => {
    // Check if user's session has an oauthState. This implies they have just returned from the auth provider.
    // Move the oauthState to component's state and clear the sessionStorage to prevent value from hanging around
    // after it has expired
    const oauthStateInSession = window.sessionStorage.getItem('oauthState');
    if (oauthStateInSession) {
      setOauthState(oauthStateInSession);
      window.sessionStorage.removeItem('oauthState');
    }
  }, []);

  useEffect(() => {
    // Check if we have a register session ID
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get(REGISTER_SESSION_ID_KEY);

    if (sessionId) {
      setRegisterSessionId(sessionId);
      // Clear value from URL
      searchParams.delete(REGISTER_SESSION_ID_KEY);
      history.replace({ search: searchParams.toString() });
    }
  }, [location.search, history]);

  const onSuccessStepContinue = async () => {
    if (credentials) {
      try {
        await loginUser(dispatch, credentials);
        scrollToTop();
        history.push(YSaatioRoutes.HOME);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const locationState: SignInPageLocationState = {
            loginError: { statusCode: e.status, errorType: e.response?.data?.errorType },
          };
          history.push(YSaatioRoutes.SIGN_IN, locationState);
        }
      }
    }
  };

  /**
   * Renders the correct step which should be shown to the user. Conditions here are in the inverse order of when
   * the user will see them, since this makes the logic easier to implement.
   */
  const renderStep = () => {
    // Registration has already been completed
    if (showSuccessStep) {
      return <YSaatioSuccessStep onContinue={onSuccessStepContinue} />;
    }
    // We have given our credentials and now only need to enter the one-time password
    if (credentials) {
      return <YSaatioOneTimePasswordStep username={credentials.username} onSuccess={() => setShowSuccessStep(true)} />;
    }
    // We have returned from the strong auth provider. Next we should to set our credentials
    if (oauthState && registerSessionId) {
      return (
        <YSaatioSetCredentialsStep
          oauthState={oauthState}
          afterSubmit={setCredentials}
          registerSessionId={registerSessionId}
        />
      );
    }
    // Registration has just begun. Go to strong auth step.
    return <YSaatioStrongAuthStep />;
  };

  return <UnauthenticatedPageContainer>{renderStep()}</UnauthenticatedPageContainer>;
};

export default YSaatioSignUpPage;
